"use client";

import React from "react";
import { useController, useFormContext } from "react-hook-form";
import { cx } from "class-variance-authority";

export interface CurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  name: string;
  label?: string;
  classNameContainer?: string;
  required?: boolean;
  noBorder?: boolean;
  noShadow?: boolean;
}

export const CurrencyInput = ({
  name,
  label,
  required = false,
  classNameContainer,
  className,
  placeholder,
  noBorder = false,
  noShadow = false,
  ...props
}: CurrencyInputProps) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController({ name, control });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    
    // Remove the dollar sign if user types it
    inputValue = inputValue.replace('$', '');
    
    // Remove all non-numeric characters except decimal point
    inputValue = inputValue.replace(/[^0-9.]/g, '');
    
    // Handle decimal point logic
    const parts = inputValue.split('.');
    if (parts.length > 2) {
      // Only allow one decimal point
      inputValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      inputValue = parts[0] + '.' + parts[1].substring(0, 2);
    }

    onChange(inputValue);
  };

  const formatDisplayValue = (val: string | number | undefined): string => {
    if (!val || val === '') return '';
    
    const stringVal = val.toString();
    const numericVal = parseFloat(stringVal);
    
    if (isNaN(numericVal)) return '';
    
    // If user is actively typing and the value doesn't end with .00, show as-is
    if (stringVal.includes('.') && stringVal !== numericVal.toFixed(2)) {
      return stringVal;
    }
    
    // For display purposes, show with .00 if it's a whole number
    return numericVal.toFixed(2);
  };

  return (
    <div className={classNameContainer}>
      {label && (
        <label className="block mb-2 text-sm text-gray-600 font-semibold">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
          $
        </span>
        <input
          {...field}
          value={value || ''}
          {...props}
          type="text"
          inputMode="decimal"
          className={cx(
            `block w-full pl-8 pr-4 py-2 mt-2 text-sm placeholder-gray-400 rounded-md`,
            !noShadow && "shadow-sm",
            `focus:border-brand-400 focus:ring-brand-400 focus:outline-none focus:ring focus:ring-opacity-40`,
            {
              "border-red-500": error && !noBorder,
              "border-gray-200": !error && !noBorder,
              "border": !noBorder,
              "text-gray-700": !props.disabled,
              "text-gray-500 bg-gray-100 cursor-default": props.disabled,
            },
            !props.disabled && !className?.includes('bg-') ? "bg-white" : "",
            className
          )}
          onChange={handleInputChange}
          onFocus={(e) => {
            // Clear default values when user starts typing
            const currentValue = e.target.value;
            if (currentValue === '0' || currentValue === '0.00' || currentValue === '') {
              onChange('');
              e.target.value = '';
            }
          }}
          onBlur={(e) => {
            // Format value on blur if it has content
            const currentValue = e.target.value;
            if (currentValue && currentValue !== '' && currentValue !== '0') {
              const formatted = formatDisplayValue(currentValue);
              onChange(formatted);
            } else if (currentValue === '0') {
              onChange('0.00');
            }
          }}
          placeholder={placeholder}
        />
      </div>
      {error && <span className="text-red-500 text-xs">{error.message}</span>}
    </div>
  );
};
